import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, ReceiptText, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, DataTable, EmptyState, LoadingState, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { cn } from "../../../lib/utils";
import sessionDB from "../../../utils/sessionDB";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import {
  CLAIM_CATEGORY,
  CLAIM_CATEGORY_LABEL_KEY,
  CLAIM_STATUS,
  CLAIM_STATUS_LABEL_KEY,
  abbreviationFor,
  claimFormPath,
  formatAmount,
  formatClaimPeriod,
  formatStamp,
  isSubmitterWritable,
  newClaimPath,
} from "../constants/claimConstants";
import { monthKeyOf } from "../lib/claimRules";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import {
  ClaimBlockedScreen,
  ClaimLoadingScreen,
  useConveyancePolicy,
} from "../components/PolicyGate";
import { selectBaseClass } from "../components/formControls";

const PAGE_SIZE = 10;
const ALL = "all";

/** Distinct claim months present in the data, newest first — the Month filter. */
function monthOptions(claims) {
  const keys = new Set();
  claims.forEach((claim) => {
    const key = monthKeyOf(claim.claimMonth || claim.claimDate);
    if (key) keys.add(key);
  });
  return Array.from(keys).sort().reverse();
}

/** The "New Claim" menu — only the categories the active policy permits. */
function NewClaimMenu({ categories, onPick, disabled }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  // A single permitted category needs no menu.
  if (categories.length === 1) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onPick(categories[0])}
        className={ui.primaryBtn}
      >
        <Plus className="h-4 w-4" />
        {t("conveyanceClaim.newClaim")}
      </button>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled || categories.length === 0}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={ui.primaryBtn}
      >
        <Plus className="h-4 w-4" />
        {t("conveyanceClaim.newClaim")}
        <ChevronDown className="h-4 w-4" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-[240px] rounded-xl border border-[var(--color-text-border)] bg-white py-1.5 shadow-[0_20px_50px_-20px_rgba(30,51,131,0.45)]"
        >
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick(category);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-foreground hover:bg-[var(--color-workspace)]/50 transition cursor-pointer"
            >
              {t(CLAIM_CATEGORY_LABEL_KEY[category])}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Screen 2 — My Conveyance Claims (ESS).
 *
 * Reads the signed-in employee's own claims from
 * GET /local-expense-claims/search/employee and lists all three categories together.
 * Which categories the New Claim control offers, and which row actions each
 * status allows, are both derived — from the active policy and from the claim
 * status respectively — never from a fixed list in this file.
 *
 * The middleware has no combined employee + status + month filter, so the
 * employee's rows are fetched once and the Status / Month / Category filters
 * and paging are applied here.
 */
export function MyConveyanceClaimsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    loading: policyLoading,
    error: policyError,
    policy,
    categories,
    currencies,
    isConfigured,
    reload: reloadPolicy,
  } = useConveyancePolicy();

  const empId = useMemo(() => {
    const fromSession = Number(sessionDB.getEmpId());
    if (Number.isFinite(fromSession) && fromSession > 0) return fromSession;
    return getAuthenticatedEmployeeId();
  }, []);

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [monthFilter, setMonthFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [page, setPage] = useState(1);


  const loadClaims = useCallback(() => {
    if (!empId) {
      setLoading(false);
      setLoadError(t("conveyanceClaim.errorNoEmployeeId"));
      return;
    }
    setLoading(true);
    setLoadError("");
    conveyanceClaimService
      .fetchMyClaims(empId)
      .then(setClaims)
      .catch((error) => {
        console.error("Failed to load conveyance claims:", error);
        setClaims([]);
        setLoadError(error.message || t("conveyanceClaim.errorLoadClaims"));
      })
      .finally(() => setLoading(false));
  }, [empId, t]);

  useEffect(() => loadClaims(), [loadClaims]);

  const months = useMemo(() => monthOptions(claims), [claims]);

  const visibleClaims = useMemo(() => {
    let rows = [...claims];
    if (statusFilter !== ALL) rows = rows.filter((row) => row.claimStatus === statusFilter);
    if (categoryFilter !== ALL) rows = rows.filter((row) => row.category === categoryFilter);
    if (monthFilter !== ALL) {
      rows = rows.filter((row) => monthKeyOf(row.claimMonth || row.claimDate) === monthFilter);
    }
    // Newest first: by claim period, then by the id the server generated.
    return rows.sort((a, b) => {
      const left = String(a.claimMonth || a.claimDate || "");
      const right = String(b.claimMonth || b.claimDate || "");
      if (left !== right) return right.localeCompare(left);
      return Number(b.claimId) - Number(a.claimId);
    });
  }, [claims, statusFilter, categoryFilter, monthFilter]);

  const totalPages = Math.max(1, Math.ceil(visibleClaims.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedClaims = useMemo(
    () => visibleClaims.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visibleClaims, page]
  );

  const filtersActive =
    statusFilter !== ALL || monthFilter !== ALL || categoryFilter !== ALL;

  const resetFilters = () => {
    setStatusFilter(ALL);
    setMonthFilter(ALL);
    setCategoryFilter(ALL);
    setPage(1);
  };

  /* ------------------------------------------------------------- gating */

  if (policyLoading) return <ClaimLoadingScreen />;

  if (policyError) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.policyUnavailableTitle")}
        message={policyError}
        onRetry={reloadPolicy}
      />
    );
  }

  if (!isConfigured) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.noPolicyTitle")}
        message={t("conveyanceClaim.noPolicyMessage")}
        onRetry={reloadPolicy}
      />
    );
  }

  const columns = [
    { key: "claimNo", label: t("conveyanceClaim.claimNo") },
    { key: "category", label: t("conveyanceClaim.claimCategory") },
    { key: "period", label: t("conveyanceClaim.periodDate") },
    { key: "amount", label: t("conveyanceClaim.totalAmount"), className: "text-right" },
    { key: "status", label: t("conveyanceClaim.status") },
    { key: "updated", label: t("conveyanceClaim.lastUpdated") },
    { key: "actions", label: t("common.actions"), className: "text-right" },
  ];

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await conveyanceClaimService.deleteClaim(pendingDelete.claimId);
      setPendingDelete(null);
      loadClaims();
    } catch (error) {
      console.error("Failed to delete claim:", error);
      setDeleteError(error.message || t("conveyanceClaim.errorDeleteClaim"));
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Edit while the submitter still owns the claim, View once an approver has
   * it, and Delete alongside Edit for the same statuses — the two where the
   * submitter still owns the claim (PRD C.7). Deleting cascades to the claim's
   * trip lines, so it is confirmed first.
   */
  const renderRowActions = (claim) => {
    const writable = isSubmitterWritable(claim.claimStatus);
    return (
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate(claimFormPath(claim.category, claim.claimId))}
          className="h-8 px-3 rounded-lg border border-[var(--color-text-border)] text-[11px] font-bold text-foreground hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary)] transition cursor-pointer"
        >
          {writable ? t("common.edit") : t("formActions.view")}
        </button>
        {writable ? (
          <button
            type="button"
            onClick={() => setPendingDelete(claim)}
            className="h-8 px-3 rounded-lg border border-rose-200 text-[11px] font-bold text-rose-600 hover:border-rose-400 hover:bg-rose-50 transition cursor-pointer"
          >
            {t("common.delete")}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <AppLayout>
      <PageBackLink to="/" />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className={ui.pageTitle}>{t("conveyanceClaim.myClaimsTitle")}</h1>
        </div>
        <div className="shrink-0 self-end sm:self-auto">
          <NewClaimMenu
            categories={categories}
            disabled={!empId}
            onPick={(category) => navigate(newClaimPath(category))}
          />
        </div>
      </div>

      {/* Deadline notice — the policy's day, surfaced where claims are raised. */}
      {policy?.submissionDeadlineDay ? (
        <p className="mb-4 text-[11px] text-[var(--color-text-secondary)]">
          {t("conveyanceClaim.deadlineNotice", { day: policy.submissionDeadlineDay })}
        </p>
      ) : null}

      {loadError ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={loadClaims}
            className="inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("localConveyance.retry")}
          </button>
        </div>
      ) : null}

      {/* Filters — Status, Month, Category, combinable */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="w-[160px]">
          <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allStatuses")}</SelectItem>
              {Object.values(CLAIM_STATUS).map((status) => (
                <SelectItem key={status} value={status}>
                  {t(CLAIM_STATUS_LABEL_KEY[status])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[160px]">
          <Select value={monthFilter} onValueChange={(value) => { setMonthFilter(value); setPage(1); }}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("conveyanceClaim.allMonths")}</SelectItem>
              {months.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[190px]">
          <Select value={categoryFilter} onValueChange={(value) => { setCategoryFilter(value); setPage(1); }}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allCategories")}</SelectItem>
              {Object.values(CLAIM_CATEGORY).map((category) => (
                <SelectItem key={category} value={category}>
                  {t(CLAIM_CATEGORY_LABEL_KEY[category])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filtersActive ? (
          <button type="button" onClick={resetFilters} className={ui.filterBtn}>
            {t("formActions.clearFilters")}
          </button>
        ) : null}
      </div>

      <ContentCard>
        {loading ? (
          <LoadingState message={t("conveyanceClaim.loadingClaims")} />
        ) : visibleClaims.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            title={
              filtersActive
                ? t("conveyanceClaim.emptyFilteredTitle")
                : t("conveyanceClaim.emptyTitle")
            }
            message={
              filtersActive
                ? t("conveyanceClaim.emptyFilteredMessage")
                : t("conveyanceClaim.emptyMessage")
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <DataTable columns={columns} minWidth="900px">
              {pagedClaims.map((claim) => (
                <tr key={claim.claimId} className="hover:bg-[var(--color-workspace)]/20 transition">
                  <td className="px-4 py-3 font-semibold text-foreground">#{claim.claimId}</td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatClaimPeriod(claim, t)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </td>
                  <td className="px-4 py-3">
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[var(--color-text-secondary)]">
                    {formatStamp(claim.modifiedOn || claim.submittedOn || claim.createdOn, t)}
                  </td>
                  <td className="px-4 py-3">{renderRowActions(claim)}</td>
                </tr>
              ))}
            </DataTable>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/40">
              {pagedClaims.map((claim) => (
                <div key={claim.claimId} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground">#{claim.claimId}</p>
                      <p className="mt-0.5 text-[12px] text-[var(--color-text-secondary)] truncate">
                        {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)} ·{" "}
                        {formatClaimPeriod(claim, t)}
                      </p>
                    </div>
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </div>

                  <p className="mt-2 text-lg font-extrabold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-secondary)]">
                    {formatStamp(claim.modifiedOn || claim.submittedOn || claim.createdOn, t)}
                  </p>

                  <div className="mt-3">{renderRowActions(claim)}</div>
                </div>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={visibleClaims.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>

      {/* Delete confirmation. A claim takes its trip lines with it, so the
          prompt names the claim rather than asking a generic "are you sure". */}
      {pendingDelete ? (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label={t("formActions.cancel")}
            onClick={() => (deleting ? null : setPendingDelete(null))}
            className="absolute inset-0 bg-[#0a1230]/50 backdrop-blur-sm cursor-default"
          />
          <div className="relative w-full max-w-[440px] rounded-2xl border border-white/60 bg-white p-6 shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)]">
            <p className="text-[17px] font-semibold text-foreground">
              {t("conveyanceClaim.deleteTitle")}
            </p>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)] leading-relaxed">
              {t("conveyanceClaim.deleteMessage", {
                claim: pendingDelete.claimNo || `#${pendingDelete.claimId}`,
              })}
            </p>
            {deleteError ? (
              <p className="mt-3 text-sm font-medium text-rose-600">{deleteError}</p>
            ) : null}
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setPendingDelete(null)}
                className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={confirmDelete}
                className="h-11 px-6 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition cursor-pointer disabled:opacity-50"
              >
                {deleting ? t("conveyanceClaim.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}

export default MyConveyanceClaimsPage;
